import * as React from 'react'
import { BookingHistory } from './BookingHistory'
import { BookingMessages } from './Messages'
import { supabase } from './supabase'
import { Button } from './ui'
import { BookingDetails, BookingCustomer, BookingDates, BookingField, BookingSection, type BookingAction } from './BookingDetails'
import { BookingEquipmentAssignment } from './BookingEquipmentAssignment'
import { LiveBookingOperations } from './LiveBookingOperations'
import { bookedAccessories } from './ProductSummary'
import { money } from './operationsModel'
import { useBookingReturn } from './useBookingReturn'
import { BookingOutcomeWarning } from './BookingOutcomeWarning'

const bookingSelect = '*,products(name,product_group,sku,product_types(assignment_mode)),inventory_allocations(state,inventory_units(id,asset_code,product_id,products(name,sku))),booking_notes(note,created_at),ops_extensions(*),booking_unit_changes(*)'
const recordedTime = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/London' })

export function OnlineBookingDialog({ bookingId, canWrite, onClose, onChanged }: { bookingId: string; canWrite: boolean; onClose: () => void; onChanged: () => void }) {
  const [booking, setBooking] = React.useState<any>(null)
  const [error, setError] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const pending = React.useRef(false)
  const [note, setNote] = React.useState('')
  const [addingNote, setAddingNote] = React.useState(false)
  const [revision, setRevision] = React.useState(0)
  const [historyRevision, setHistoryRevision] = React.useState(0)
  React.useEffect(() => {
    let stale = false
    const load = async () => {
      try {
        const { data, error } = await supabase.from('bookings').select(bookingSelect).eq('id', bookingId).maybeSingle()
        if (stale) return
        if (error || !data) throw new Error(error?.message || 'That booking could not be loaded.')
        setBooking(data)
        setError('')
      } catch (error) {
        if (!stale) setError(error instanceof Error ? error.message : 'That booking could not be loaded.')
      }
    }
    void load()
    return () => { stale = true }
  }, [bookingId, revision])
  const saved = async () => { setRevision(value => value + 1); onChanged() }
  const returned = useBookingReturn(booking ? { source: 'online', id: booking.id, state: booking.state, updated_at: booking.updated_at } : null, canWrite && !busy, saved)
  const working = busy || returned.busy
  const mutate = async (name: string, args: Record<string, unknown>) => {
    if (working || pending.current || !canWrite) return
    pending.current = true
    setBusy(true); setError('')
    try {
      const { error } = await supabase.rpc(name, { p_booking_id: bookingId, ...args })
      if (error) throw error
      setNote(''); setAddingNote(false)
      await saved()
    } catch (error) {
      const message = error && typeof error === 'object' && 'message' in error ? String(error.message) : 'The action could not be completed.'
      setError(name === 'admin_cancel_live_booking' && message.includes('BOOKING_CHANGED_REFRESH')
        ? 'This booking has changed. Close and reopen it before cancelling.'
        : name === 'admin_cancel_live_booking' && message.includes('BOOKING_NOT_CANCELLABLE')
          ? 'This booking can no longer be cancelled. Close and reopen it to see its current status.'
          : message)
    }
    finally { pending.current = false; setBusy(false) }
  }
  if (!booking) return <BookingDetails reference="Booking details" onClose={onClose}>
    {error ? <div role="alert"><p>{error}</p><Button type="button" onClick={() => setRevision(value => value + 1)}>Retry</Button></div> : <p role="status">Loading booking…</p>}
  </BookingDetails>
  const currentDates = {
    start: booking.operational_delivery_date || booking.delivery_date,
    end: booking.operational_hire_end_date || booking.hire_end_date,
    collection: booking.operational_collection_date || booking.collection_date,
  }
  const datesChanged = currentDates.start !== booking.delivery_date
    || currentDates.end !== booking.hire_end_date
    || currentDates.collection !== booking.collection_date
  const address = booking.delivery_address
  const addressText = typeof address === 'string' ? address : [address?.line1, address?.line2, address?.city, address?.state, address?.postal_code || address?.postcode || booking.postcode].filter(Boolean).join('\n')
  const actions: BookingAction[] = canWrite ? [{ label: 'Add note', onClick: () => setAddingNote(true) }] : []
  if (canWrite && booking.state === 'payment_pending') actions.push({ label: 'Cancel checkout', variant: 'destructive', onClick: () => void mutate('admin_cancel_unpaid_booking', {}) })
  if (canWrite && booking.state === 'manual_review') actions.push({ label: 'Confirm booking', onClick: () => void mutate('admin_resolve_manual_review', { p_action: 'confirm', p_note: note }) })
  if (canWrite && ['unassigned', 'confirmed', 'manual_review'].includes(booking.state)) actions.push({
    label: 'Cancel booking', variant: 'destructive', onClick: () => {
      if (working || pending.current) return
      if (window.confirm(`Cancel booking ${booking.booking_reference} and release any reserved equipment? This does not issue a refund or change the recorded payment status. Arrange any refund separately, for example by bank transfer.`)) {
        void mutate('admin_cancel_live_booking', { p_expected_updated_at: booking.updated_at, p_note: note })
      }
    },
  })
  if (canWrite && booking.deposit_pence > 0) for (const state of ['refunded', 'retained', 'manual_review'] as const) actions.push({
    label: `Mark deposit ${state.replace('_', ' ')}`, onClick: () => void mutate('admin_set_deposit_state', { p_state: state, p_note: note }),
  })
  return <LiveBookingOperations booking={booking} canWrite={canWrite && !working} onSaved={saved} onInvoiceChanged={() => setHistoryRevision(value => value + 1)}>{(operationActions, extensions) =>
    <BookingDetails equipment={{ productType: booking.products?.product_group, quantity: booking.quantity }} reference={booking.booking_reference} status={booking.state} payment={booking.payment_state} actions={[...operationActions, ...(returned.action ? [returned.action] : []), ...actions]} busy={working} onClose={onClose}>
      {(error || returned.error) && <p className="message error" role="alert">{error || returned.error}</p>}
      <BookingOutcomeWarning state={booking.state} payment={booking.payment_state} />
      <BookingCustomer name={booking.customer_name} phone={booking.customer_phone} email={booking.customer_email} address={addressText} />
      <BookingMessages target={{ kind: 'online', id: booking.id }} canWrite={canWrite} />
      <BookingSection title="Booking details">
        <BookingDates online {...currentDates} />
        {datesChanged && <section aria-label="Original booking"><h4>Original booking</h4>
          <BookingDates online start={booking.delivery_date} end={booking.hire_end_date} collection={booking.collection_date} />
        </section>}
        <BookingEquipmentAssignment booking={booking} />
        {bookedAccessories(booking.line_items).length > 0 && <p className="booking-muted">Accessories: {bookedAccessories(booking.line_items).map(item => item.label).join(', ')}</p>}
        {booking.manual_review_reason && <div className="review-reason"><strong>Manual review</strong><p>{booking.manual_review_reason}</p></div>}
      </BookingSection>
      {extensions}
      <BookingSection title="Payment">
        <dl className="booking-fields"><BookingField label="Original booking total">{money(booking.total_pence)}</BookingField><BookingField label="Deposit">{money(booking.deposit_pence)} · {booking.deposit_state?.replaceAll('_', ' ')}</BookingField></dl>
        {booking.stripe_payment_intent_id && <a href={`https://dashboard.stripe.com/payments/${booking.stripe_payment_intent_id}`} target="_blank" rel="noreferrer">Open in Stripe ↗</a>}
        <details><summary>Price breakdown</summary><div className="line-list">{(booking.line_items ?? []).map((item: any) => <div key={item.key}><span>{item.label}</span><strong>{money(item.amountPence)}</strong></div>)}</div></details>
      </BookingSection>
      <BookingSection title="Internal notes">
        <div className="notes">{booking.booking_notes?.length ? [...booking.booking_notes].sort((a: any, b: any) => a.created_at.localeCompare(b.created_at)).map((item: any, index: number) => <p key={`${item.created_at}:${index}`}><span>{recordedTime.format(new Date(item.created_at))}</span>{item.note}</p>) : <p className="booking-muted">No notes recorded.</p>}</div>
      </BookingSection>
      <BookingHistory key={booking.id} source="online" bookingId={booking.id} refreshKey={`${revision}:${booking.updated_at}:${historyRevision}`} />
      {addingNote && canWrite && <form className="notes" onSubmit={event => { event.preventDefault(); if (note.trim()) void mutate('admin_add_booking_note', { p_note: note }) }}>
        <label htmlFor="booking-note">New internal note</label><textarea id="booking-note" autoFocus value={note} maxLength={4000} disabled={working} onChange={event => setNote(event.target.value)} />
        <div className="action-row"><Button className="primary" disabled={working || !note.trim()}>Save note</Button><Button type="button" disabled={working} onClick={() => setAddingNote(false)}>Cancel</Button></div>
      </form>}
    </BookingDetails>
  }</LiveBookingOperations>
}
